import { computed } from 'vue';
import { Course } from 'src/modules/Course/models/Course';
import { UserCourse } from 'src/modules/Course/models/UserCourse';
import { useRepo } from 'pinia-orm';
import { useStrapiPost } from 'src/shared/composables/useStrapiApi';
import type { ErrorResponse, Response } from 'src/types/Response';
import { useStrapiGet } from 'shared/composables/useStrapiApi';
import { addCourseSectionOrderFieldToCourse } from 'shared/composables/useAssignments';
import { strapiApiPageLimit } from 'shared/composables/useAssignments';
import { StrapiPagination } from 'src/models/StrapiPagination';

const courseStoreRepo = useRepo(Course);
const userCourseRepo = useRepo(UserCourse);
const userStrapiPaginationRepo = useRepo(StrapiPagination);
const courseStorePagination = computed(() => {
  const pagination = userStrapiPaginationRepo
    .where('contentType', 'courseStore')
    .first();

  if (pagination) return pagination;

  return userStrapiPaginationRepo.insert({
    contentType: 'courseStore',
    pageSize: strapiApiPageLimit,
    start: 0,
    limit: strapiApiPageLimit,
  });
});

export function useCourseStore() {
  const courses = computed(() => {
    return courseStoreRepo
      .all()
      .filter(
        (course, index, self) =>
          index ===
          self.findIndex(
            (c) => c.documentId === course.documentId && c.is_show_in_store,
          ),
      );
  });

  const {
    execute: executeCourses,
    data: dataCourses,
    error: errorCourses,
  } = useStrapiGet('courses');

  async function fetchCourseStore() {
    if (courseStorePagination.value.isLast) {
      console.log('fetchCourseStore: isLastCourseStore');
      return;
    }

    const params = {
      'filters[is_show_in_store]': true,
      'populate[tags][fields][0]': 'name',
      'populate[image][fields][0]': 'url',
      'populate[image][fields][1]': 'name',
      'populate[image][fields][2]': 'caption',
      'populate[image][fields][3]': 'alternativeText',
      'pagination[start]': courseStorePagination.value?.start ?? 0,
      'pagination[limit]':
        courseStorePagination.value?.limit ?? strapiApiPageLimit,
    };

    try {
      await executeCourses({ params });
      if (!errorCourses.value && dataCourses.value) {
        const orderedCourses = addCourseSectionOrderFieldToCourse(
          dataCourses.value.data,
        );
        courseStoreRepo.save(orderedCourses);
        if (courseStorePagination.value?.id) {
          userStrapiPaginationRepo.save({
            id: courseStorePagination.value?.id,
            total: dataCourses.value?.meta?.pagination.total,
            start:
              (courseStorePagination.value?.start ?? 0) +
              (dataCourses.value.data?.length ?? 0),
          });
        }
      }
    } catch (err) {
      console.log(`fetchCourseStore: Error fetching courses: ${err as string}`);
      throw err;
    }
  }

  const isOwnedCourse = (course: Course): boolean => {
    // Depend on repoVersion to force reactivity
    return course?.user_course_id !== null;
  };

  function getUserCourseByCourse(course: Course): UserCourse | null {
    return userCourseRepo.find(course?.user_course_id ?? -1);
  }

  async function assignCourseToUser(
    courseId: number,
    userId: number,
  ): Promise<Response> {
    if (!courseId || !userId) {
      console.log('CourseId and UserId are required');
      return {
        success: false,
        error: { message: 'CourseId and UserId are required' },
      };
    }

    const userCourseData = {
      user: {
        connect: [{ id: userId }],
      },
      course: {
        connect: [{ id: courseId }],
      },
    };
    const { execute, data, error } = useStrapiPost('user-courses', {
      data: userCourseData,
    });
    await execute();
    if (error.value) {
      console.log(
        'Error assigning course:',
        (error.value as ErrorResponse)?.message,
      );
      return {
        success: false,
        error: error.value as ErrorResponse,
      };
    }
    if (data.value) {
      userCourseRepo.save({
        ...data.value.data,
        course: { id: courseId },
        user: { id: userId },
      });
    }
    // Optionally, you can fetch the updated list of courses after assignment
    console.log('Course assigned successfully');
    return {
      success: true,
      data: data.value.data,
    };
  }

  return {
    courseStorePagination,
    courses,
    assignCourseToUser,
    fetchCourseStore,
    isOwnedCourse,
    getUserCourseByCourse,
  };
}
